// "N stores without a stage": stores that belong to the pipeline (their own pipelineId, or their type's default) but
// whose stageId is not one of its stages, usually after a stage was deleted on a phone. The board has no column for
// them, so they are listed here, each opening its store, with a way on to the Stores list for this pipeline.

import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import type { RecordPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import type { StoreRowLookups } from '@/features/common';
import { Button, Dialog } from '@/ui';
import { pipelineTitle, VIRTUALIZE_AFTER, withoutStageText } from './boardModel';
import { CardList } from './CardList';

export interface WithoutStageDialogProps {
  open: boolean;
  onClose: () => void;
  pipeline: { name: string };
  records: readonly RecordPayload[];
  itemOf: (id: string) => DataItem<RecordPayload> | undefined;
  lookups: StoreRowLookups;
  /** The Stores list filtered to this pipeline (and owner). */
  storesHref: string;
}

export function WithoutStageDialog({ open, onClose, pipeline, records, itemOf, lookups, storesHref }: WithoutStageDialogProps) {
  const name = pipelineTitle(pipeline);
  const virtualized = records.length > VIRTUALIZE_AFTER;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={withoutStageText(records.length)}
      description={`These stores are on the ${name} pipeline but in none of its stages (no stage yet, or one that was deleted), so the board has no column for them. Open a store to see it, or work through them in the Stores list.`}
      flush
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Link
            to={storesHref}
            onClick={onClose}
            // Styled as the kit's primary Button: it navigates, so it is a real link (open in a new tab works).
            className="gt-primary-fill inline-flex h-9 shrink-0 items-center gap-2 rounded-control px-4 text-sm font-semibold whitespace-nowrap text-primary-contrast shadow-primary hover:bg-primary-hover"
          >
            Open {name} in Stores
            <ArrowRight aria-hidden className="size-4" />
          </Link>
        </>
      }
    >
      {records.length === 0 ? (
        <p className="px-6 py-6 text-sm text-fg-muted">Every store on this pipeline has a stage.</p>
      ) : (
        <CardList
          records={records}
          itemOf={itemOf}
          lookups={lookups}
          label={`Stores without a stage in ${name}`}
          virtualized={virtualized}
          // A virtual list needs a box of its own to scroll; a short one lets the dialog body scroll instead.
          className={virtualized ? 'h-[min(60dvh,560px)] px-4' : 'px-4'}
        />
      )}
    </Dialog>
  );
}
